import React from "react";
import {
  Autocomplete,
  Checkbox,
  Chip,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";

const ITEM_HEIGHT = 48;
const DEFAULT_MAX_HEIGHT = 360;
const SELECT_ALL_VALUE = "all";
const GROUP_PREFIX = "group:";

const VirtualizedConfigContext = React.createContext({
  itemSize: ITEM_HEIGHT,
  maxHeight: DEFAULT_MAX_HEIGHT,
});

const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(
  props,
  ref,
) {
  const { children, className, ...other } = props;
  const { itemSize, maxHeight } = React.useContext(VirtualizedConfigContext);

  const optionChildren = React.Children.toArray(children);
  const listHeight = Math.min(optionChildren.length * itemSize, maxHeight);

  return (
    <div ref={ref} className={className} {...other}>
      <List
        height={listHeight}
        width="100%"
        itemCount={optionChildren.length}
        itemSize={itemSize}
        innerElementType="ul"
      >
        {({ index, style }) => {
          const child = optionChildren[index];
          return React.cloneElement(child, {
            style: { ...style, ...child.props.style },
          });
        }}
      </List>
    </div>
  );
});

export default function VirtualizedMultiSelect({
  label,
  id,
  value,
  onChange,
  options = [],
  groups,
  disabled = false,
  width,
  minWidth = 160,
  marginTop = 2,
  paperWidth,
  showSelectAll = false,
  selectAllChecked,
  selectAllIndeterminate,
  selectAllText = "Select All",
  formControlSx,
  itemSize = ITEM_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
}) {
  const currentValue = value || [];
  const displayValue = currentValue.filter((item) => item !== SELECT_ALL_VALUE);
  const [inputValue, setInputValue] = React.useState("");

  const grouped = Boolean(groups && groups.length > 0);
  const groupsArr = groups || [];

  const groupedOptions = React.useMemo(
    () => Array.from(new Set(groupsArr.flatMap((g) => g.options || []))),
    [groupsArr],
  );
  const flatOptions = grouped ? groupedOptions : options;

  const allSelected = grouped
    ? flatOptions.length > 0 && flatOptions.every((item) => displayValue.includes(item))
    : Boolean(options.length > 0 && displayValue.length === options.length);
  const partiallySelected = grouped
    ? displayValue.some((item) => flatOptions.includes(item)) && !allSelected
    : Boolean(displayValue.length > 0 && displayValue.length < options.length);
  const isAllChecked =
    selectAllChecked !== undefined ? selectAllChecked : allSelected;
  const isAllIndeterminate =
    selectAllIndeterminate !== undefined
      ? selectAllIndeterminate
      : partiallySelected;

  const isGroupSentinel = (item) =>
    typeof item === "string" && item.startsWith(GROUP_PREFIX);

  const handleChange = (_event, newValue) => {
    const raw = newValue || [];
    if (grouped) {
      const groupClick = raw.find(isGroupSentinel);
      if (groupClick) {
        const label = groupClick.slice(GROUP_PREFIX.length);
        const g = groupsArr.find((x) => x.label === label);
        if (g) {
          const groupFull =
            g.options.length > 0 &&
            g.options.every((d) => displayValue.includes(d));
          onChange?.({
            target: {
              value: groupFull
                ? displayValue.filter((d) => !g.options.includes(d))
                : Array.from(new Set(displayValue.concat(g.options))),
            },
          });
        }
        return;
      }
      if (raw.includes(SELECT_ALL_VALUE)) {
        onChange?.({
          target: { value: allSelected ? [] : [...flatOptions] },
        });
        return;
      }
      onChange?.({
        target: {
          value: raw.filter((v) => v !== SELECT_ALL_VALUE && !isGroupSentinel(v)),
        },
      });
      return;
    }
    if (allSelected) {
      onChange?.({ target: { value: [] } });
    } else {
      onChange?.({ target: { value: raw } });
    }
  };

  const handleInputChange = (_event, newInputValue, reason) => {
    if (reason === "input") {
      setInputValue(newInputValue);
    } else if (reason === "clear") {
      setInputValue("");
    }
    // ignore "reset" (selection) to preserve filter text
  };

  const groupedFilterOptions = (_filtered, state) => {
    const filterText = (state.inputValue ?? inputValue).toLowerCase().trim();
    const result = [];
    if (showSelectAll) result.push(SELECT_ALL_VALUE);
    groupsArr.forEach((g) => {
      const labelMatch = g.label.toLowerCase().includes(filterText);
      const matching = g.options.filter((d) =>
        d.toLowerCase().includes(filterText),
      );
      if (labelMatch || matching.length) {
        result.push(`${GROUP_PREFIX}${g.label}`);
        result.push(...(labelMatch ? g.options : matching));
      }
    });
    return result;
  };

  const filterOptions = (filtered, state) => {
    if (grouped) return groupedFilterOptions(filtered, state);
    const filterText = state.inputValue ?? inputValue;
    const result = options.filter((item) =>
      item.toLowerCase().includes(filterText.toLowerCase()),
    );
    return showSelectAll ? [SELECT_ALL_VALUE, ...result] : result;
  };

  const handleClearAll = () => onChange?.({ target: { value: [] } });

  const renderTags = (tagValue, getTagProps) => {
    if (allSelected) {
      return [
        <Chip
          label="All"
          {...getTagProps({ index: 0 })}
          onDelete={handleClearAll}
          key="all"
        />,
      ];
    }
    if (grouped) {
      const chips = [];
      const covered = new Set();
      groupsArr.forEach((g) => {
        if (g.options.length && g.options.every((d) => tagValue.includes(d))) {
          chips.push({
            label: g.label,
            remove: (cur) => cur.filter((d) => !g.options.includes(d)),
          });
          g.options.forEach((d) => covered.add(d));
        }
      });
      tagValue.forEach((d) => {
        if (!covered.has(d)) {
          chips.push({
            label: d,
            remove: (cur) => cur.filter((x) => x !== d),
          });
        }
      });
      return chips.map((c, i) => (
        <Chip
          key={`${c.label}-${i}`}
          label={c.label}
          {...getTagProps({ index: i })}
          onDelete={() =>
            onChange?.({ target: { value: c.remove(tagValue) } })
          }
        />
      ));
    }
    return tagValue.map((option, index) => (
      <Chip label={option} {...getTagProps({ index })} key={option} />
    ));
  };

  const getOptionLabel = (option) => {
    if (option === SELECT_ALL_VALUE) return selectAllText;
    if (isGroupSentinel(option)) return option.slice(GROUP_PREFIX.length);
    return option;
  };

  const renderOption = (props, option) => {
    if (option === SELECT_ALL_VALUE) {
      return (
        <li {...props} key={SELECT_ALL_VALUE}>
          <ListItemIcon>
            <Checkbox
              checked={isAllChecked}
              indeterminate={isAllIndeterminate}
            />
          </ListItemIcon>
          <ListItemText primary={selectAllText} />
        </li>
      );
    }
    if (isGroupSentinel(option)) {
      const g = groupsArr.find(
        (x) => x.label === option.slice(GROUP_PREFIX.length),
      );
      const checked =
        g.options.length > 0 && g.options.every((d) => displayValue.includes(d));
      const indeterminate = !checked && g.options.some((d) => displayValue.includes(d));
      return (
        <li
          {...props}
          key={option}
          style={{ backgroundColor: "rgba(0,0,0,0.04)" }}
        >
          <ListItemIcon>
            <Checkbox checked={checked} indeterminate={indeterminate} />
          </ListItemIcon>
          <Typography variant="subtitle2" fontWeight={600}>
            {g.label}
          </Typography>
        </li>
      );
    }
    return (
      <li {...props} key={`${option}-${props["data-option-index"] ?? 0}`}>
        <Checkbox checked={displayValue.indexOf(option) > -1} />
        <ListItemText primary={option} />
      </li>
    );
  };

  const autocompleteOptions = grouped
    ? [
        SELECT_ALL_VALUE,
        ...groupsArr.flatMap((g) => [
          `${GROUP_PREFIX}${g.label}`,
          ...g.options,
        ]),
      ]
    : showSelectAll
      ? [SELECT_ALL_VALUE, ...options]
      : options;

  return (
    <VirtualizedConfigContext.Provider value={{ itemSize, maxHeight }}>
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        limitTags={grouped ? 24 : 3}
        disabled={disabled}
        options={autocompleteOptions}
        value={displayValue}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        filterOptions={filterOptions}
        renderTags={renderTags}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            sx={formControlSx}
          />
        )}
        ListboxComponent={VirtualizedListbox}
        sx={{ width, minWidth, marginTop }}
      />
    </VirtualizedConfigContext.Provider>
  );
}